import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Circle, MoreVertical, Plus, Edit, Trash2, X, Monitor, Cpu, HardDrive, Clock, Building2, Terminal, Camera, Play, Send, Loader2, Download, AlertCircle, RefreshCw } from 'lucide-react';
import API from '../api';
import { useActiveOrg } from '../contexts/ActiveOrgContext';

const Robots = () => {
    const { t, i18n } = useTranslation();
    const { activeOrg } = useActiveOrg();
    const [robots, setRobots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(null);
    const [editingRobot, setEditingRobot] = useState(null);
    const [formData, setFormData] = useState({ name: '', hostname: '', ip_address: '', organization: '' });
    const [showMenu, setShowMenu] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [activeConsoleRobot, setActiveConsoleRobot] = useState(null);
    const [consoleTab, setConsoleTab] = useState('terminal'); // 'terminal' | 'screenshot'
    const [consoleCode, setConsoleCode] = useState('print("Merhaba " + agent.config.get("name", "Robot"))\nimport sys\nprint("Python sürümü:", sys.version)');
    const [consoleTerminalHistory, setConsoleTerminalHistory] = useState([]);
    const [consoleLoading, setConsoleLoading] = useState(false);
    const [screenshotLoading, setScreenshotLoading] = useState(false);
    const [screenshotImage, setScreenshotImage] = useState(null);
    const [screenshotStatusMsg, setScreenshotStatusMsg] = useState('');

    useEffect(() => {
        fetchData();
    }, [activeOrg]); // activeOrg değiştiğinde yeniden fetch

    const fetchData = async () => {
        try {
            const [robotsRes, orgsRes, meRes] = await Promise.all([
                API.get('/robots/'),
                API.get('/organizations/'),
                API.get(`/me/?_t=${Date.now()}`).catch(() => ({ data: { is_superuser: false } }))
            ]);
            // Robotları aktif organizasyona göre filtrele
            let filteredRobots = robotsRes.data;
            if (activeOrg) {
                filteredRobots = robotsRes.data.filter(robot =>
                    robot.organization === activeOrg.id || robot.organization_name === activeOrg.name
                );
            }
            setRobots(filteredRobots);
            setOrganizations(orgsRes.data);
            setCurrentUser(meRes.data);
            setLoading(false);
        } catch (err) {
            setError(t('robots.error_list'));
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const data = { ...formData };
            if (!data.organization) delete data.organization;

            if (editingRobot) {
                await API.patch(`/robots/${editingRobot.id}/`, data);
            } else {
                await API.post('/robots/', data);
            }
            setShowModal(false);
            setEditingRobot(null);
            setFormData({ name: '', hostname: '', ip_address: '', organization: '' });
            fetchData();
        } catch (err) {
            console.error("Error:", err);
            setError(t('common.error'));
        }
    };

    const handleEdit = (robot) => {
        setEditingRobot(robot);
        setFormData({
            name: robot.name || '',
            hostname: robot.hostname || '',
            ip_address: robot.ip_address || '',
            organization: robot.organization || ''
        });
        setShowModal(true);
        setShowMenu(null);
    };

    const handleDelete = async (id) => {
        const confirmMsg = i18n.language && i18n.language.startsWith('en')
            ? 'Are you sure you want to delete this robot?'
            : 'Bu robotu silmek istediğinize emin misiniz?';
        if (window.confirm(confirmMsg)) {
            try {
                await API.delete(`/robots/${id}/`);
                fetchData();
            } catch (err) {
                setError(t('common.error'));
            }
        }
        setShowMenu(null);
    };

    const openAddModal = () => {
        setEditingRobot(null);
        setFormData({ name: '', hostname: '', ip_address: '', organization: '' });
        setShowModal(true);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const locale = i18n.language && i18n.language.startsWith('en') ? 'en-US' : 'tr-TR';
        return new Date(dateStr).toLocaleString(locale);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'online': return 'bg-green-100 text-green-800';
            case 'busy': return 'bg-blue-100 text-blue-800';
            case 'error': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('robots.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('robots.subtitle')}</p>
                </div>
                {/* Manuel robot ekleme butonu kaldırıldı (agent/runner check-in ile otomatik eklenir) */}
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={18} /></button>
                </div>
            )}

            {/* Premium Agent & Runner Download Banner (Sadece Superuser görebilir) */}
            {currentUser && currentUser.is_superuser && (
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 rounded-2xl p-6 shadow-xl mb-8 text-white border border-indigo-800/40 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="space-y-2 max-w-2xl">
                        <div className="flex items-center gap-3">
                            <span className="bg-blue-500 text-white font-mono text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider shadow-sm">{t('robots.stable_version')}</span>
                            <h2 className="text-xl font-extrabold tracking-tight">{t('robots.download_banner_title')}</h2>
                        </div>
                        <p className="text-slate-300 text-xs leading-relaxed">
                            {t('robots.download_banner_desc')}
                        </p>
                        <div className="flex flex-wrap gap-4 pt-1 text-slate-400 text-xs">
                            <div className="flex items-center gap-1.5">
                                <Cpu size={14} className="text-blue-400" /> <span>{t('robots.windows_compat')}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Terminal size={14} className="text-indigo-400" /> <span>{t('robots.tray_compat')}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 w-full md:w-auto">
                        <a
                            href="/orchestrator/agent-download/"
                            className="flex-1 md:flex-none px-5 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition-all border border-blue-400/20 group"
                            title="Ajan kurulum ve aktivasyon aracı"
                        >
                            <Download size={18} className="group-hover:-translate-y-0.5 transition-transform" />
                            <div>
                                <div className="text-sm font-bold">{t('robots.download_agent')}</div>
                                <div className="text-[10px] text-blue-200 font-normal">Aktivasyon & GUI (111 MB)</div>
                            </div>
                        </a>
                        <a
                            href="/orchestrator/runner-download/"
                            className="flex-1 md:flex-none px-5 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl shadow-lg shadow-slate-900/50 flex items-center justify-center gap-2 transition-all border border-slate-700 group"
                            title="Arka plan / Başlangıç çalıştırma aracı"
                        >
                            <Download size={18} className="group-hover:-translate-y-0.5 transition-transform" />
                            <div>
                                <div className="text-sm font-bold">{t('robots.download_runner')}</div>
                                <div className="text-[10px] text-slate-300 font-normal">Sistem Tepsisi / Tray Icon (106 MB)</div>
                            </div>
                        </a>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>
                ) : robots.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">{t('robots.no_robots')}</div>
                ) : (
                    <div className="overflow">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('robots.col_robot')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('robots.col_host_ip')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('robots.col_org')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('robots.col_system')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('robots.last_seen')}</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {robots.map((robot) => (
                                    <tr key={robot.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                                                    <Bot size={20} />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{robot.name || t('robots.unnamed')}</div>
                                                    <div className="text-xs text-gray-500">ID: {robot.id} | Agent: {robot.agent_version || '-'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{robot.hostname || '-'}</div>
                                            <div className="text-xs text-gray-500">{robot.ip_address || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{robot.organization_name || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => setShowDetailModal(robot)}
                                                className="text-xs text-blue-600 hover:underline"
                                            >
                                                {robot.os_info ? (
                                                    <span className="flex items-center">
                                                        <Monitor size={12} className="mr-1" />
                                                        {robot.os_info.substring(0, 20)}...
                                                    </span>
                                                ) : t('robots.view_details')}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full flex items-center w-fit ${getStatusColor(robot.status)}`}>
                                                <Circle size={8} fill="currentColor" className="mr-1" />
                                                {robot.status === 'online' ? t('robots.status_online') : robot.status === 'busy' ? t('robots.status_busy') : t('robots.status_offline')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(robot.last_checkin)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                                            {currentUser?.is_superuser && (
                                                <button
                                                    onClick={() => { setActiveConsoleRobot(robot); setScreenshotImage(null); setScreenshotStatusMsg(''); }}
                                                    className="inline-flex items-center mr-3 p-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg transition-all"
                                                    title={t('robots.dev_panel')}
                                                >
                                                    <Terminal size={15} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setShowMenu(showMenu === robot.id ? null : robot.id)}
                                                className="text-gray-400 hover:text-gray-600 inline-block align-middle"
                                            >
                                                <MoreVertical size={18} />
                                            </button>
                                            {showMenu === robot.id && (
                                                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                                    {currentUser?.is_superuser && (
                                                        <button
                                                            onClick={() => { setActiveConsoleRobot(robot); setScreenshotImage(null); setScreenshotStatusMsg(''); setShowMenu(null); }}
                                                            className="w-full px-4 py-2 text-left text-sm text-indigo-700 hover:bg-indigo-50 flex items-center font-medium border-b border-indigo-50"
                                                        >
                                                            <Terminal size={14} className="mr-2 text-indigo-600" /> {t('robots.dev_panel')}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => { setShowDetailModal(robot); setShowMenu(null); }}
                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                                    >
                                                        <Monitor size={14} className="mr-2" /> {t('robots.details')}
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(robot)}
                                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                                    >
                                                        <Edit size={14} className="mr-2" /> {t('common.edit')}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(robot.id)}
                                                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                                                    >
                                                        <Trash2 size={14} className="mr-2" /> {t('common.delete')}
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Robot Detay Modalı */}
            {showDetailModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-900">{showDetailModal.name || t('robots.details_title')}</h2>
                            <button onClick={() => setShowDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs text-gray-500">{t('robots.hostname')}</p>
                                    <p className="font-medium">{showDetailModal.hostname || '-'}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs text-gray-500">{t('robots.ip_address')}</p>
                                    <p className="font-medium">{showDetailModal.ip_address || '-'}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs text-gray-500">{t('common.status')}</p>
                                    <p className="font-medium capitalize">{showDetailModal.status || 'offline'}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs text-gray-500">{t('robots.agent_version')}</p>
                                    <p className="font-medium">{showDetailModal.agent_version || '-'}</p>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                    <Monitor size={16} className="mr-2" /> {t('robots.system_info')}
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex items-center text-sm">
                                        <span className="text-gray-500 w-32">{t('robots.os')}</span>
                                        <span className="text-gray-900">{showDetailModal.os_info || '-'}</span>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="text-gray-500 w-32">{t('robots.cpu')}</span>
                                        <span className="text-gray-900">{showDetailModal.cpu_info || '-'}</span>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="text-gray-500 w-32">{t('robots.ram')}</span>
                                        <span className="text-gray-900">{showDetailModal.ram_total ? `${showDetailModal.ram_total} GB` : '-'}</span>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="text-gray-500 w-32">{t('robots.python')}</span>
                                        <span className="text-gray-900">{showDetailModal.python_version || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                    <Building2 size={16} className="mr-2" /> {t('common.organizations')}
                                </h3>
                                <p className="text-sm text-gray-900">{showDetailModal.organization_name || t('robots.unassigned')}</p>
                            </div>

                            <div className="border-t pt-4">
                                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                    <Clock size={16} className="mr-2" /> {t('robots.time_info')}
                                </h3>
                                <div className="text-sm">
                                    <span className="text-gray-500">{t('robots.last_seen')}: </span>
                                    <span className="text-gray-900">{formatDate(showDetailModal.last_checkin)}</span>
                                </div>
                            </div>

                            {showDetailModal.jobs && showDetailModal.jobs.length > 0 && (
                                <div className="border-t pt-4">
                                    <h3 className="font-semibold text-gray-900 mb-3">{t('robots.jobs_count', { count: showDetailModal.jobs.length })}</h3>
                                    <div className="space-y-2">
                                        {showDetailModal.jobs.map(job => (
                                            <div key={job.id} className="bg-gray-50 p-2 rounded text-sm">
                                                {job.name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {editingRobot ? t('robots.edit_robot') : t('robots.add_robot')}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('robots.name')}</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Robot-1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('robots.hostname')}</label>
                                <input
                                    type="text"
                                    value={formData.hostname}
                                    onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="workstation-01"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('robots.ip_address')}</label>
                                <input
                                    type="text"
                                    value={formData.ip_address}
                                    onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="192.168.1.100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.organizations')}</label>
                                <select
                                    value={formData.organization}
                                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">{t('robots.select_optional')}</option>
                                    {organizations.map(org => (
                                        <option key={org.id} value={org.id}>{org.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => { setShowModal(false); setEditingRobot(null); }}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {editingRobot ? t('common.save') : t('common.add')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Developer Remote Console Drawer/Modal */}
            {activeConsoleRobot && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-end z-50 transition-opacity">
                    <div className="bg-white h-screen w-full max-w-2xl shadow-2xl flex flex-col animate-slide-left border-l border-gray-100">
                        {/* Header */}
                        <div className="p-6 bg-gradient-to-r from-slate-900 to-indigo-950 text-white flex justify-between items-center shadow-md">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="p-1.5 bg-indigo-500/20 rounded-lg text-indigo-400">
                                        <Terminal size={20} />
                                    </span>
                                    <h2 className="text-xl font-bold tracking-tight">{t('robots.dev_console')}</h2>
                                </div>
                                <p className="text-slate-400 text-xs mt-1.5">
                                    Robot: <span className="text-white font-semibold">{activeConsoleRobot.name}</span> (ID: {activeConsoleRobot.id}) | Host: {activeConsoleRobot.hostname}
                                </p>
                            </div>
                            <button 
                                onClick={() => setActiveConsoleRobot(null)} 
                                className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-xl"
                            >
                                <X size={22} />
                            </button>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="flex border-b border-gray-100 bg-slate-50 p-2">
                            <button
                                onClick={() => setConsoleTab('terminal')}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                                    consoleTab === 'terminal'
                                        ? 'bg-white text-indigo-700 shadow-sm border border-gray-100'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                    }`}
                            >
                                <Terminal size={16} />
                                {t('robots.live_terminal')}
                            </button>
                            <button
                                onClick={() => setConsoleTab('screenshot')}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                                    consoleTab === 'screenshot'
                                        ? 'bg-white text-indigo-700 shadow-sm border border-gray-100'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                }`}
                            >
                                <Camera size={16} />
                                {t('robots.live_screenshot')}
                            </button>
                        </div>

                        {/* Body Panel */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {consoleTab === 'terminal' ? (
                                <div className="flex flex-col h-full space-y-4">
                                    {/* Terminal Console Output */}
                                    <div className="flex-1 min-h-[220px] bg-slate-950 rounded-xl p-4 font-mono text-xs text-slate-200 overflow-y-auto shadow-inner border border-slate-800 space-y-2">
                                        <div className="text-emerald-400 font-bold border-b border-slate-800 pb-2 flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                            Secure Reverse Shell Connection - READY
                                        </div>
                                        {consoleTerminalHistory.length === 0 ? (
                                            <div className="text-slate-500 italic py-4 text-center">{t('robots.no_commands')}</div>
                                        ) : (
                                            consoleTerminalHistory.map((h, i) => (
                                                <div key={i} className="border-b border-slate-900 pb-3 last:border-0 last:pb-0">
                                                    <div className="flex items-center text-indigo-400 font-semibold gap-1.5 mb-1">
                                                        <span>>>></span>
                                                        <span>{t('robots.code_block')} ({new Date(h.timestamp).toLocaleTimeString()})</span>
                                                    </div>
                                                    <pre className="bg-slate-900/50 p-2 rounded text-slate-300 border border-slate-900 mb-2 overflow-x-auto whitespace-pre">{h.code}</pre>
                                                    
                                                    {h.status === 'pending' ? (
                                                        <div className="flex items-center text-amber-400 gap-1.5">
                                                            <Loader2 size={12} className="animate-spin" />
                                                            <span>{t('robots.waiting_checkin')}</span>
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-1">
                                                            <div className="text-slate-400 font-semibold text-[10px] uppercase tracking-wider">{t('robots.result')} <span className={h.status === 'success' ? 'text-emerald-400' : 'text-rose-400'}>{h.status}</span></div>
                                                            {h.stdout && <pre className="text-emerald-400 whitespace-pre-wrap">{h.stdout}</pre>}
                                                            {h.stderr && <pre className="text-rose-400 whitespace-pre-wrap font-semibold bg-rose-950/20 p-2 rounded border border-rose-950/30">{h.stderr}</pre>}
                                                            {!h.stdout && !h.stderr && <div className="text-slate-500 italic">{t('robots.empty_output')}</div>}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* Editor input */}
                                    <div className="space-y-2">
                                        <label className="block text-xs font-semibold text-slate-700">{t('robots.code_to_run')}</label>
                                        <textarea
                                            value={consoleCode}
                                            onChange={(e) => setConsoleCode(e.target.value)}
                                            rows={6}
                                            className="w-full bg-slate-900 text-slate-100 font-mono text-xs rounded-xl p-4 border border-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none shadow-md"
                                            placeholder="print('Hello world!')"
                                        />
                                    </div>

                                    {/* Run Button */}
                                    <div className="flex justify-between items-center">
                                        <button
                                            onClick={() => setConsoleTerminalHistory([])}
                                            className="px-3 py-1.5 text-xs text-slate-500 hover:text-slate-800 rounded-lg border border-slate-200 hover:bg-slate-50 transition-all font-semibold"
                                        >
                                            {t('robots.clear_console')}
                                        </button>
                                        <button
                                            disabled={consoleLoading}
                                            onClick={async () => {
                                                if (!consoleCode.strip?.() && !consoleCode.trim()) return;
                                                setConsoleLoading(true);
                                                const historyId = Date.now();
                                                const newHistoryItem = {
                                                    id: historyId,
                                                    code: consoleCode,
                                                    status: 'pending',
                                                    stdout: '',
                                                    stderr: '',
                                                    timestamp: new Date().toISOString()
                                                };
                                                setConsoleTerminalHistory(prev => [newHistoryItem, ...prev]);

                                                try {
                                                    const res = await API.post(`/robots/${activeConsoleRobot.id}/command-request/`, { code: consoleCode });
                                                    const cmdId = res.data.command_id;
                                                    
                                                    // Start polling
                                                    let attempts = 0;
                                                    const interval = setInterval(async () => {
                                                        attempts++;
                                                        try {
                                                            const statusRes = await API.get(`/robots/${activeConsoleRobot.id}/command-status/?command_id=${cmdId}`);
                                                            const data = statusRes.data;
                                                            if (data.status === 'executed' || data.status === 'failed') {
                                                                clearInterval(interval);
                                                                setConsoleTerminalHistory(prev => prev.map(item => 
                                                                    item.id === historyId 
                                                                        ? { ...item, status: data.status === 'executed' ? 'success' : 'failed', stdout: data.stdout, stderr: data.stderr }
                                                                        : item
                                                                ));
                                                                setConsoleLoading(false);
                                                            }
                                                        } catch (err) {
                                                            console.error(err);
                                                        }

                                                        if (attempts > 30) {
                                                            clearInterval(interval);
                                                            const timeoutError = i18n.language && i18n.language.startsWith('en')
                                                                ? 'Timeout (Robot did not respond in 45 seconds).'
                                                                : 'Zaman aşımı (45 saniye robot yanıt vermedi).';
                                                            setConsoleTerminalHistory(prev => prev.map(item => 
                                                                    item.id === historyId 
                                                                        ? { ...item, status: 'failed', stderr: timeoutError }
                                                                        : item
                                                            ));
                                                            setConsoleLoading(false);
                                                        }
                                                    }, 1500);

                                                } catch (err) {
                                                    const connError = i18n.language && i18n.language.startsWith('en')
                                                        ? 'Connection error occurred.'
                                                        : 'Bağlantı hatası oluştu.';
                                                    setConsoleTerminalHistory(prev => prev.map(item => 
                                                        item.id === historyId 
                                                            ? { ...item, status: 'failed', stderr: err.response?.data?.error || connError }
                                                            : item
                                                    ));
                                                    setConsoleLoading(false);
                                                }
                                            }}
                                            className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-bold shadow-md shadow-indigo-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {consoleLoading ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    {t('robots.running')}
                                                </>
                                            ) : (
                                                <>
                                                    <Play size={14} fill="currentColor" />
                                                    {t('robots.run_code')}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6 flex flex-col items-center">
                                    <div className="w-full bg-slate-50 p-4 rounded-xl border border-gray-100 flex justify-between items-center">
                                        <div>
                                            <h3 className="font-bold text-slate-800 text-sm">{t('robots.take_screenshot')}</h3>
                                            <p className="text-slate-500 text-xs mt-1">{t('robots.screenshot_desc')}</p>
                                        </div>
                                        <button
                                            disabled={screenshotLoading}
                                            onClick={async () => {
                                                const startMsg = i18n.language && i18n.language.startsWith('en')
                                                    ? 'Creating screen capture request...'
                                                    : 'Ekran görüntüsü talebi oluşturuluyor...';
                                                const waitMsg = i18n.language && i18n.language.startsWith('en')
                                                    ? 'Waiting for robot check-in (max 45s)...'
                                                    : 'Robotun check-in yapması bekleniyor (maks. 45sn)...';
                                                
                                                setScreenshotLoading(true);
                                                setScreenshotStatusMsg(startMsg);
                                                setScreenshotImage(null);
                                                
                                                try {
                                                    const res = await API.post(`/robots/${activeConsoleRobot.id}/screenshot-request/`);
                                                    const reqId = res.data.request_id;
                                                    setScreenshotStatusMsg(waitMsg);
                                                    
                                                    let attempts = 0;
                                                    const interval = setInterval(async () => {
                                                        attempts++;
                                                        try {
                                                            const statusRes = await API.get(`/robots/${activeConsoleRobot.id}/screenshot-status/?request_id=${reqId}`);
                                                            const data = statusRes.data;
                                                            if (data.status === 'completed') {
                                                                clearInterval(interval);
                                                                setScreenshotImage(data.image_url);
                                                                setScreenshotStatusMsg('');
                                                                setScreenshotLoading(false);
                                                            } else if (data.status === 'failed') {
                                                                clearInterval(interval);
                                                                const failedMsg = i18n.language && i18n.language.startsWith('en')
                                                                    ? 'Screen capture operation failed on the robot side.'
                                                                    : 'Ekran görüntüsü alma işlemi robot tarafında başarısız oldu.';
                                                                setScreenshotStatusMsg(failedMsg);
                                                                setScreenshotLoading(false);
                                                            }
                                                        } catch (err) {
                                                            console.error(err);
                                                        }

                                                        if (attempts > 30) {
                                                            clearInterval(interval);
                                                            const timeoutMsg = i18n.language && i18n.language.startsWith('en')
                                                                ? 'Timeout. Robot may not be active or desktop session was not found.'
                                                                : 'Zaman aşımı. Robot aktif olmayabilir veya masaüstü oturumu bulunamadı.';
                                                            setScreenshotStatusMsg(timeoutMsg);
                                                            setScreenshotLoading(false);
                                                        }
                                                    }, 1500);
                                                } catch (err) {
                                                    const initFailedMsg = i18n.language && i18n.language.startsWith('en')
                                                        ? 'Could not initiate screen capture request.'
                                                        : 'Ekran görüntüsü talebi başlatılamadı.';
                                                    setScreenshotStatusMsg(err.response?.data?.error || initFailedMsg);
                                                    setScreenshotLoading(false);
                                                }
                                            }}
                                            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md flex items-center gap-2 transition-all disabled:opacity-50"
                                        >
                                            {screenshotLoading ? (
                                                <>
                                                    <Loader2 size={16} className="animate-spin" />
                                                    {i18n.language && i18n.language.startsWith('en') ? 'Triggering...' : 'Tetikleniyor...'}
                                                </>
                                            ) : (
                                                <>
                                                    <Camera size={16} />
                                                    {i18n.language && i18n.language.startsWith('en') ? 'Capture Screen' : 'Görüntü Yakala'}
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {screenshotStatusMsg && (
                                        <div className="p-4 bg-slate-50 text-slate-700 rounded-xl border border-slate-200 flex items-center gap-3 w-full animate-pulse">
                                            <Loader2 size={18} className="animate-spin text-indigo-600 flex-shrink-0" />
                                            <span className="text-sm font-semibold">{screenshotStatusMsg}</span>
                                        </div>
                                    )}

                                    {screenshotImage ? (
                                        <div className="w-full space-y-4">
                                            <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-lg bg-slate-900 group">
                                                <img 
                                                    src={screenshotImage} 
                                                    alt="Robot Live Screen" 
                                                    className="w-full h-auto max-h-[400px] object-contain mx-auto"
                                                />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <a 
                                                        href={screenshotImage} 
                                                        target="_blank" 
                                                        rel="noreferrer"
                                                        className="px-4 py-2 bg-white text-slate-950 font-bold rounded-xl shadow-md hover:bg-slate-100 transition-all text-xs"
                                                    >
                                                        {i18n.language && i18n.language.startsWith('en') ? 'Open Full Size' : 'Tam Boyutta Aç'}
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <a
                                                    href={screenshotImage}
                                                    download={`robot_${activeConsoleRobot.id}_screen.png`}
                                                    className="px-4 py-2 bg-slate-900 text-white hover:bg-slate-800 font-bold rounded-xl shadow-md flex items-center gap-2 transition-all text-sm"
                                                >
                                                    <Download size={15} />
                                                    {i18n.language && i18n.language.startsWith('en') ? 'Download File' : 'Dosyayı İndir'}
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        !screenshotLoading && !screenshotStatusMsg && (
                                            <div className="py-12 flex flex-col items-center justify-center text-slate-400 max-w-sm text-center space-y-3">
                                                <div className="p-4 bg-slate-50 rounded-full text-slate-400">
                                                    <Camera size={36} />
                                                </div>
                                                <h4 className="font-bold text-slate-700 text-sm">{i18n.language && i18n.language.startsWith('en') ? 'No Image' : 'Görüntü Yok'}</h4>
                                                <p className="text-xs">
                                                    {i18n.language && i18n.language.startsWith('en')
                                                        ? 'Click "Capture Screen" button above to capture a live screenshot from the robot.'
                                                        : 'Yukarıdaki "Görüntü Yakala" butonuna basarak robottan anlık canlı ekran görüntüsü alabilirsiniz.'}
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Robots;
